import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Logo } from "../../components/Brand";
import { api, errMsg, fmtDate, money, PLANS } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const Kpi = ({ label, value, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5"><p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p><p className="mt-2 text-2xl font-bold font-heading" data-testid={testid}>{value}</p></div>
);
const STATE = { trial: "bg-sky-50 text-sky-700", active: "bg-emerald-50 text-emerald-700", read_only: "bg-amber-50 text-amber-700", locked: "bg-red-50 text-red-700", suspended: "bg-red-100 text-red-800", unverified: "bg-slate-100 text-slate-600" };
const Pill = ({ s }) => <span className={`px-2 py-0.5 rounded text-xs font-semibold ${STATE[s] || ""}`}>{s?.replace("_", " ")}</span>;

function Overview() {
  const [m, setM] = useState(null);
  useEffect(() => {
    api.get("/admin/metrics").then((r) => setM(r.data));
  }, []);
  if (!m) return null;
  return (
    <div className="space-y-4" data-testid="admin-metrics">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="MRR (recurring)" value={money(m.mrr)} testid="metric-mrr" />
        <Kpi label="ARR (recurring)" value={money(m.arr)} testid="metric-arr" />
        <Kpi label="Cash collected (all time)" value={money(m.cash_collected_total)} testid="metric-cash-total" />
        <Kpi label="Cash collected (30 days)" value={money(m.cash_collected_30d)} testid="metric-cash-30d" />
        <Kpi label="Registered organisations" value={m.total_orgs} testid="metric-total-orgs" />
        <Kpi label="Active free trials" value={m.active_trials} testid="metric-trials" />
        <Kpi label="Active paying customers" value={m.paying} testid="metric-paying" />
        <Kpi label="Active user licences" value={m.active_licences} testid="metric-licences" />
        <Kpi label="Monthly subscribers" value={m.plan_counts.monthly} testid="metric-monthly" />
        <Kpi label="Six-month subscribers" value={m.plan_counts.six_month} testid="metric-six-month" />
        <Kpi label="Annual subscribers" value={m.plan_counts.annual} testid="metric-annual" />
        <Kpi label="Upcoming renewals (14d)" value={m.upcoming_renewals} testid="metric-renewals" />
        <Kpi label="Expired (read-only)" value={m.expired} testid="metric-expired" />
        <Kpi label="Locked (60+ days)" value={m.locked} testid="metric-locked" />
        <Kpi label="Suspended" value={m.suspended} testid="metric-suspended" />
        <Kpi label="Failed payments (30d)" value={m.failed_payments_30d} testid="metric-failed" />
      </div>
      <p className="text-xs text-slate-500">MRR/ARR show normalised recurring revenue from active subscriptions (six-month and annual plans divided across their term). Cash collected shows actual paid invoice totals, including upfront payments.</p>
    </div>
  );
}

function OrgDetail({ id, onClose, onChange }) {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  const { refresh } = useAuth();
  const enter = async () => {
    try {
      await api.post(`/admin/orgs/${id}/support/enter`);
      await refresh();
      nav("/app");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const load = useCallback(() => api.get(`/admin/orgs/${id}`).then((r) => setD(r.data)), [id]);
  useEffect(() => {
    load();
  }, [load]);
  const act = async (path, body, msg) => {
    try {
      await api.post(`/admin/orgs/${id}/${path}`, body);
      toast.success(msg);
      load();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-org-dialog">
        {d && <>
          <DialogHeader><DialogTitle className="font-heading">{d.org.name} <span className="font-mono text-xs text-slate-500">{d.org.tenant_id}</span></DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><p className="text-xs text-slate-500">Status</p><Pill s={d.access.state} /></div>
            <div><p className="text-xs text-slate-500">Plan</p><p className="font-semibold">{d.org.plan ? PLANS[d.org.plan].label : "Trial"}</p></div>
            <div><p className="text-xs text-slate-500">Ends / renews</p><p className="font-semibold">{fmtDate(d.access.ends_at)}</p></div>
            <div><p className="text-xs text-slate-500">MRR</p><p className="font-semibold">{money(d.mrr)}</p></div>
            <div><p className="text-xs text-slate-500">Seats</p><p className="font-semibold">{d.users.filter((u) => u.active).length} / {d.org.seats_purchased}{(d.org.free_seat_grants || []).filter((g) => !g.ended && new Date(g.until) > new Date()).reduce((a, g) => a + g.seats, 0) ? ` + ${(d.org.free_seat_grants || []).filter((g) => !g.ended && new Date(g.until) > new Date()).reduce((a, g) => a + g.seats, 0)} free` : ""}</p></div>
            <div><p className="text-xs text-slate-500">Payment</p><p className="font-semibold">{d.org.payment_failed ? "Failed" : d.org.subscription_status || "—"}</p></div>
            <div><p className="text-xs text-slate-500">Contact</p><p className="font-semibold truncate">{d.org.contact_email}</p></div>
            <div><p className="text-xs text-slate-500">Created</p><p className="font-semibold">{fmtDate(d.org.created_at)}</p></div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button disabled={!d.org.support_access} onClick={enter} className="bg-fuchsia-700 text-white font-semibold px-4 py-2 rounded-md text-sm disabled:opacity-40" data-testid="admin-support-enter-btn">Log in as this organisation</button>
            {!d.org.support_access && <span className="text-xs text-slate-500" data-testid="admin-support-not-allowed">Their administrator must allow support access in Settings first.</span>}
            {d.org.suspended
              ? <button onClick={() => act("reactivate", { reason: "Owner reactivation" }, "Organisation reactivated")} className="bg-emerald-600 text-white font-semibold px-4 py-2 rounded-md text-sm" data-testid="admin-reactivate-btn">Reactivate</button>
              : <button onClick={() => window.confirm("Suspend this organisation?") && act("suspend", { reason: "Owner suspension" }, "Organisation suspended")} className="bg-red-600 text-white font-semibold px-4 py-2 rounded-md text-sm" data-testid="admin-suspend-btn">Suspend</button>}
          </div>
          <Sweeteners d={d} act={act} reload={() => { load(); onChange(); }} />
          <div className="border border-dashed border-slate-300 rounded-md p-3">
            <p className="text-xs font-semibold text-slate-600 mb-2">Testing tool — simulate dates (staging only)</p>
            <div className="flex flex-wrap gap-2">
              {[["trial_ending", "Ends in 20h"], ["expire", "Expired 1 day ago"], ["lockout_soon", "Expired 55 days ago"], ["lock", "Expired 61 days ago"], ["restore", "Restore +5 days"]].map(([k, l]) => (
                <button key={k} onClick={() => act("simulate", { scenario: k }, `Simulated: ${l}`)} className="border border-slate-300 px-3 py-1.5 rounded text-xs font-semibold" data-testid={`admin-sim-${k}`}>{l}</button>
              ))}
            </div>
          </div>
          <div><p className="font-semibold text-sm mb-2">Users</p>{d.users.map((u) => <p key={u.id} className="text-sm text-slate-600">{u.name} · {u.email} · {u.role} {u.active ? "" : "(inactive)"}</p>)}</div>
          <div><p className="font-semibold text-sm mb-2">Record counts (content not shown)</p><p className="text-sm text-slate-600">{Object.entries(d.record_counts).map(([k, v]) => `${k}: ${v}`).join(" · ") || "No records"}</p></div>
          <div><p className="font-semibold text-sm mb-2">Invoices</p>{d.invoices.map((i) => <p key={i.id} className="text-sm text-slate-600">{fmtDate(i.created)} · {i.number} · {money(i.amount_paid)} · {i.status}</p>)}{!d.invoices.length && <p className="text-sm text-slate-500">None</p>}</div>
        </>}
      </DialogContent>
    </Dialog>
  );
}

function Sweeteners({ d, act, reload }) {
  const [m, setM] = useState({ months: 1, reason: "" });
  const [u, setU] = useState({ users: 1, months: 3, reason: "" });
  const grants = (d.org.free_seat_grants || []).filter((g) => !g.ended);
  const remove = async (gid) => {
    try {
      await api.delete(`/admin/orgs/${d.org.id}/free-users/${gid}`);
      toast.success("Free users removed");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="border border-emerald-200 bg-emerald-50/40 rounded-md p-3 space-y-3" data-testid="admin-sweeteners">
      <p className="text-xs font-semibold text-emerald-800">Sweeteners</p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Free months<input type="number" min={1} max={24} value={m.months} onChange={(e) => setM({ ...m, months: Number(e.target.value) })} className="field h-8 w-20 mt-1" data-testid="admin-free-months-input" /></label>
        <input placeholder="Reason (optional)" value={m.reason} onChange={(e) => setM({ ...m, reason: e.target.value })} className="field h-8 flex-1 min-w-[10rem]" data-testid="admin-free-months-reason" />
        <button onClick={() => act("free-months", m, `${m.months} free month(s) added`)} className="bg-emerald-600 text-white text-xs font-semibold px-3 h-8 rounded" data-testid="admin-free-months-btn">Give free months</button>
      </div>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs">Free users<input type="number" min={1} max={50} value={u.users} onChange={(e) => setU({ ...u, users: Number(e.target.value) })} className="field h-8 w-20 mt-1" data-testid="admin-free-users-input" /></label>
        <label className="text-xs">For months<input type="number" min={1} max={24} value={u.months} onChange={(e) => setU({ ...u, months: Number(e.target.value) })} className="field h-8 w-20 mt-1" data-testid="admin-free-users-months-input" /></label>
        <input placeholder="Reason (optional)" value={u.reason} onChange={(e) => setU({ ...u, reason: e.target.value })} className="field h-8 flex-1 min-w-[10rem]" data-testid="admin-free-users-reason" />
        <button onClick={() => act("free-users", u, `${u.users} free user(s) added`)} className="bg-emerald-600 text-white text-xs font-semibold px-3 h-8 rounded" data-testid="admin-free-users-btn">Add free users</button>
      </div>
      {grants.map((g) => <p key={g.id} className="text-xs text-slate-700" data-testid="admin-free-users-grant">{g.seats} free user(s) until {fmtDate(g.until)} · {g.reason} <button onClick={() => remove(g.id)} className="text-red-700 font-semibold ml-2" data-testid={`admin-free-users-remove-${g.id}`}>Remove</button></p>)}
      {(d.org.free_month_grants || []).map((g) => <p key={g.id} className="text-xs text-slate-600">{fmtDate(g.at)} · {g.months} free month(s) · {g.reason} · {g.how}</p>)}
      {d.org.free_months_credit > 0 && <p className="text-xs text-slate-600">{d.org.free_months_credit} month(s) credit waiting for their next checkout</p>}
      {d.org.referral_code_used && <p className="text-xs text-slate-600">Signed up with referral code {d.org.referral_code_used}</p>}
      <button onClick={() => act("ai-complimentary", { on: !d.org.ai_addon?.complimentary }, d.org.ai_addon?.complimentary ? "Complimentary QORDEL AI removed" : "QORDEL AI add-on now complimentary")} className="text-xs font-semibold text-sky-700" data-testid="admin-org-ai-comp-btn">{d.org.ai_addon?.complimentary ? "Remove complimentary QORDEL AI add-on" : "Make QORDEL AI add-on complimentary"}</button>
    </div>
  );
}

function AiAdmin() {
  const [d, setD] = useState(null);
  const [f, setF] = useState(null);
  const load = () => api.get("/admin/ai").then((r) => { setD(r.data); setF({ price_aud: r.data.settings.price_aud, available: r.data.settings.available !== false }); });
  useEffect(() => { load(); }, []);
  if (!d || !f) return <p className="text-sm text-slate-500">Loading…</p>;
  const save = async () => {
    try {
      await api.put("/admin/ai", f);
      toast.success("AI add-on settings saved");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const comp = async (oid, on) => {
    await api.post(`/admin/orgs/${oid}/ai-complimentary`, { on });
    load();
  };
  return (
    <div className="space-y-6" data-testid="admin-ai">
      <div className="bg-white border border-slate-200 rounded-xl p-6 max-w-xl space-y-3">
        <h3 className="font-semibold">QORDEL AI Integration add-on</h3>
        <p className="text-sm text-slate-600">Customers pay this monthly integration fee to QORDEL and pay Anthropic directly for Claude usage with their own API key. New price applies to new add-on subscriptions.</p>
        <label className="block text-sm">Monthly price (AUD)<input type="number" min={0} step="0.5" value={f.price_aud} onChange={(e) => setF({ ...f, price_aud: Number(e.target.value) })} className="field mt-1 max-w-[10rem]" data-testid="admin-ai-price-input" /></label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.available} onChange={(e) => setF({ ...f, available: e.target.checked })} data-testid="admin-ai-available-toggle" />Add-on available to customers</label>
        <button onClick={save} className="bg-sky-600 text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="admin-ai-save-btn">Save</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-ai-orgs-table"><thead className="bg-slate-50 text-left"><tr>{["Organisation", "Add-on", "Key connected", "Enabled", "Questions (month)", "Tokens (month)", ""].map((h) => <th key={h} className="px-4 py-2 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{d.orgs.map((o) => <tr key={o.org_id} className="border-t border-slate-100"><td className="px-4 py-2">{o.name}</td><td className="px-4 py-2">{o.complimentary ? "Complimentary" : o.addon_status || "—"}</td><td className="px-4 py-2">{o.connected ? "Yes" : "No"}</td><td className="px-4 py-2">{o.enabled ? "Yes" : "No"}</td><td className="px-4 py-2">{o.requests}</td><td className="px-4 py-2">{o.total_tokens.toLocaleString()}</td><td className="px-4 py-2"><button onClick={() => comp(o.org_id, !o.complimentary)} className="text-xs font-semibold text-sky-700" data-testid={`admin-ai-comp-${o.org_id}`}>{o.complimentary ? "Remove complimentary" : "Make complimentary"}</button></td></tr>)}</tbody>
        </table>
        {!d.orgs.length && <p className="p-6 text-sm text-slate-500">No organisations have set up QORDEL AI yet. Use "Make complimentary" from an organisation once it appears, or after it connects a key.</p>}
      </div>
    </div>
  );
}

function Orgs() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get("/admin/orgs", { params: { q, state } }).then((r) => setRows(r.data)), [q, state]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-200">
        <div className="relative flex-1"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organisation, tenant ID or email" className="field pl-9" data-testid="admin-org-search" /></div>
        <select value={state} onChange={(e) => setState(e.target.value)} className="field sm:w-48" data-testid="admin-org-state-filter"><option value="">All statuses</option>{Object.keys(STATE).map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}</select>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-orgs-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">Organisation</th><th className="px-4 py-3">Tenant</th><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Seats</th><th className="px-4 py-3">MRR</th><th className="px-4 py-3">Ends / renews</th></tr></thead>
          <tbody>{rows.map((o) => (
            <tr key={o.id} onClick={() => setOpen(o.id)} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`admin-org-row-${o.tenant_id}`}>
              <td className="px-4 py-3 font-medium">{o.name}</td><td className="px-4 py-3 font-mono text-xs">{o.tenant_id}</td><td className="px-4 py-3">{o.plan ? PLANS[o.plan].label : "Trial"}</td><td className="px-4 py-3"><Pill s={o.access.state} /></td><td className="px-4 py-3">{o.seats_used}/{o.seats_purchased}</td><td className="px-4 py-3">{money(o.mrr)}</td><td className="px-4 py-3">{fmtDate(o.access.ends_at)}</td>
            </tr>))}</tbody>
        </table>
      </div>
      {open && <OrgDetail id={open} onClose={() => setOpen(null)} onChange={load} />}
    </div>
  );
}

function SupportAdmin() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/support").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const update = async (id, body) => {
    try {
      await api.put(`/admin/support/${id}`, body);
      toast.success("Ticket updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="admin-support-list">
      {rows.map((r) => (
        <div key={r.id} className="p-4 space-y-2">
          <div className="flex flex-wrap justify-between gap-2"><p className="font-semibold">{r.subject}</p><select value={r.status} onChange={(e) => update(r.id, { status: e.target.value })} className="field h-8 w-36"><option value="open">open</option><option value="in_progress">in progress</option><option value="resolved">resolved</option><option value="closed">closed</option></select></div>
          <p className="text-xs text-slate-500">{r.org_name} · {r.user_email} · {r.category} · {fmtDate(r.created_at)}</p>
          <p className="text-sm text-slate-700">{r.message}</p>
          <input defaultValue={r.response} onBlur={(e) => e.target.value !== r.response && update(r.id, { response: e.target.value })} placeholder="Response to customer (saved on blur)" className="field" />
        </div>
      ))}
      {!rows.length && <p className="p-6 text-sm text-slate-500">No support requests.</p>}
    </div>
  );
}

function LogTable({ path, cols, testid }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(path).then((r) => setRows(r.data));
  }, [path]);
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{cols.map(([k, l]) => <th key={k} className="px-4 py-3">{l}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-slate-100">{cols.map(([k]) => <td key={k} className="px-4 py-2 text-slate-700 max-w-xs truncate">{k === "ts" || k === "created_at" ? (r[k] ? new Date(r[k]).toLocaleString("en-AU") : "") : String(r[k] ?? "")}</td>)}</tr>)}</tbody>
      </table>
      {!rows.length && <p className="p-6 text-sm text-slate-500" data-testid={`${testid}-empty`}>Nothing here yet.</p>}
    </div>
  );
}

function Backups() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/backups").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try {
      const r = await fn();
      toast.success(typeof msg === "function" ? msg(r.data) : msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy("");
  };
  return (
    <div className="space-y-4" data-testid="admin-backups">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600 max-w-2xl">Full database backups run daily at 02:30 AEST (16:30 UTC) to private object storage. "Test restore" restores a backup into a temporary database, checks every collection's record count, then deletes the temporary copy. Live data is never overwritten.</p>
        <button disabled={!!busy} onClick={() => run("run", () => api.post("/admin/backups/run"), "Backup started — refresh in a minute")} className="bg-sky-600 text-white font-semibold px-4 py-2 rounded-md text-sm" data-testid="admin-backup-run-btn">{busy === "run" ? "Backing up…" : "Run backup now"}</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-backups-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Trigger</th><th className="px-4 py-3">Collections</th><th className="px-4 py-3">Documents</th><th className="px-4 py-3">Restore test</th><th className="px-4 py-3" /></tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} className="border-t border-slate-100">
              <td className="px-4 py-2">{new Date(b.ts).toLocaleString("en-AU")}</td><td className="px-4 py-2">{b.trigger}</td><td className="px-4 py-2">{Object.keys(b.counts).length}</td><td className="px-4 py-2">{Object.values(b.counts).reduce((s, n) => s + n, 0)}</td>
              <td className="px-4 py-2">{b.verified ? (b.verified.ok ? <span className="text-emerald-700 font-semibold">Passed {fmtDate(b.verified.ts)}</span> : <span className="text-red-700 font-semibold">Mismatch</span>) : "—"}</td>
              <td className="px-4 py-2 text-right"><button disabled={!!busy} onClick={() => run(b.id, () => api.post(`/admin/backups/${b.id}/verify`), (d) => (d.ok ? "Restore test passed" : "Restore test found mismatches"))} className="text-sky-700 font-semibold" data-testid={`admin-backup-verify-${b.id}`}>{busy === b.id ? "Testing…" : "Test restore"}</button></td>
            </tr>))}</tbody>
        </table>
        {!rows.length && <p className="p-6 text-sm text-slate-500">No backups yet.</p>}
      </div>
    </div>
  );
}

export default function Admin() {
  const { logout } = useAuth();
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <header className="bg-[#0A192F]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4"><Logo light size={28} /><span className="text-xs font-semibold tracking-widest uppercase text-sky-400">Platform Owner</span></div>
          <button onClick={async () => { await logout(); nav("/login"); }} className="text-slate-300 hover:text-white inline-flex items-center gap-2 text-sm" data-testid="admin-logout-btn"><LogOut className="w-4 h-4" />Log out</button>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Tabs defaultValue="overview">
          <TabsList className="flex-wrap h-auto">
            {[["overview", "Overview"], ["orgs", "Organisations"], ["support", "Support"], ["audit", "Audit log"], ["emails", "Email log"], ["referrals", "Referrals"], ["ai", "QORDEL AI"], ["backups", "Backups"]].map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`admin-tab-${k}`}>{l}</TabsTrigger>)}
          </TabsList>
          <TabsContent value="overview" className="mt-6"><Overview /></TabsContent>
          <TabsContent value="orgs" className="mt-6"><Orgs /></TabsContent>
          <TabsContent value="support" className="mt-6"><SupportAdmin /></TabsContent>
          <TabsContent value="audit" className="mt-6"><LogTable path="/admin/audit-logs" testid="admin-audit-table" cols={[["ts", "When"], ["user_email", "User"], ["action", "Action"], ["tenant_id", "Tenant"], ["detail", "Detail"]]} /></TabsContent>
          <TabsContent value="emails" className="mt-6"><LogTable path="/admin/emails" testid="admin-email-table" cols={[["ts", "When"], ["to", "To"], ["template", "Template"], ["status", "Status"], ["link", "Link (staging only)"]]} /></TabsContent>
          <TabsContent value="referrals" className="mt-6"><LogTable path="/admin/referrals" testid="admin-referrals-table" cols={[["created_at", "Signed up"], ["referrer_name", "Referred by"], ["referred_name", "New organisation"], ["status", "Status"], ["plan", "Plan"]]} /></TabsContent>
          <TabsContent value="backups" className="mt-6"><Backups /></TabsContent>
          <TabsContent value="ai" className="mt-6"><AiAdmin /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
